import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SchemaDefinitionForm, {
  SchemaDefinitionField,
} from '@/features/adp/components/shared/SchemaDefinitionForm'
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks'
import {
  DestinationConfigurationOverlayData,
  openOverlay,
  popOverlay,
} from '@/features/adp/state/overlaySlice'
import { useTestConnectionMutation } from '@/features/adp/mutations/useConnectionMutations'
import { useCreateTargetDataSourceMutation } from '@/features/adp/mutations/useDestinationMutations'
import { useTargetData } from '@/features/adp/hooks/useTargetData'
import {
  getAdpSessionNode,
  mergeAdpSessionNode,
  useAdpSessionStore,
} from '@/features/adp/hooks/useAdpSessionStore'
import type { BaseDatabaseConfig } from '@/features/adp/services/common/common.types'
import { DataSourceWithExtras } from '@/features/adp/services/common/common.types'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/ui/sheet'
import PickerDetailsHeader from '@/features/adp/components/shared/PickerDetailsHeader'
import { Button } from '@/ui/button'
import { ChevronLeft } from 'lucide-react'
import styles from './DestinationSheets.module.scss'
import { useQueryClient } from '@tanstack/react-query'
import { store } from '@/app/store'
import { syncCanvasState } from '@/features/adp/state/canvas/canvasSlice'
import { DataSourceType } from '@/features/adp/types/data-source.types'

export type PersistedDestinationConfigurationState = {
  isConfigured: boolean
  isReadOnly: boolean
  values: Record<string, string>
  schemaFields: SchemaDefinitionField[]
  postTestFields: SchemaDefinitionField[]
  submitted_destination_payload: BaseDatabaseConfig & Record<string, unknown>
  data_source_instance: string
  data_source_type: string | null
}

type DestinationNodeScopedState = {
  postTestFields: SchemaDefinitionField[]
  isConnectionTested: boolean
  isBaseFormValid: boolean
  isCombinedFormValid: boolean
}

const EMPTY_DESTINATION_SCHEMA: SchemaDefinitionField[] = []

const DestinationConfiguration = () => {
  const { isOpen, type, data } = useAppSelector(state => state.overlay)
  const isDestinationConfigurationOpen = isOpen && type === 'destination-configuration'
  const destinationConfigurationData = isDestinationConfigurationOpen
    ? (data as DestinationConfigurationOverlayData)
    : null
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { setTargetData } = useTargetData()
  const { getNode } = useAdpSessionStore()
  const formContainerRef = useRef<HTMLDivElement | null>(null)
  const isNavigatingBackRef = useRef(false)
  /** Form values updated on every keystroke — kept in a ref so the sheet does not re-render per key. */
  const latestValuesByNodeRef = useRef<Record<string, Record<string, string>>>({})
  const nodeKey = destinationConfigurationData?.nodeId ?? '__destination_configuration_closed__'
  const nodeKeyRef = useRef(nodeKey)
  nodeKeyRef.current = nodeKey

  const persistedNodeState = destinationConfigurationData?.nodeId
    ? (
        getNode(destinationConfigurationData.nodeId) as {
          destinationConfigurationState?: PersistedDestinationConfigurationState
        } | null
      )?.destinationConfigurationState
    : null

  const [nodeScopedState, setNodeScopedState] = useState<
    Record<string, DestinationNodeScopedState>
  >({})

  const currentNodeState = nodeScopedState[nodeKey] ?? {
    postTestFields: persistedNodeState?.postTestFields ?? [],
    isConnectionTested: persistedNodeState?.isConfigured ?? false,
    isBaseFormValid: false,
    isCombinedFormValid: false,
  }

  const postTestFields = currentNodeState.postTestFields
  const isConnectionTested = currentNodeState.isConnectionTested
  const isBaseFormValid = currentNodeState.isBaseFormValid
  const isCombinedFormValid = currentNodeState.isCombinedFormValid

  useEffect(() => {
    if (!destinationConfigurationData?.nodeId) return
    const persistedValues = persistedNodeState?.values
    if (persistedValues != null && Object.keys(persistedValues).length > 0) {
      latestValuesByNodeRef.current[nodeKey] = { ...persistedValues }
    }
  }, [destinationConfigurationData?.nodeId, nodeKey, persistedNodeState?.values])

  const shouldShowBackButton = useMemo(() => {
    return destinationConfigurationData?.isOpenedDirectly !== true
  }, [destinationConfigurationData?.isOpenedDirectly])

  const targetData: DataSourceWithExtras[] = queryClient.getQueryData(['sidebar-data'])?.[1] ?? []
  const selectedTarget = useMemo(() => {
    const sourceId = destinationConfigurationData?.sourceId
    if (sourceId == null) return null
    return targetData.find(target => target?.data_source_id === Number(sourceId)) ?? null
  }, [destinationConfigurationData?.sourceId, targetData])

  const effectiveDestinationData: DestinationConfigurationOverlayData | null = useMemo(() => {
    if (!destinationConfigurationData) return null

    if (destinationConfigurationData.isOpenedDirectly) {
      return {
        ...destinationConfigurationData,
        ...(selectedTarget ?? destinationConfigurationData),
      }
    }

    const hasFullDetails = destinationConfigurationData.data_source_id != null
    if (hasFullDetails) return destinationConfigurationData

    return {
      ...destinationConfigurationData,
      ...(selectedTarget ?? destinationConfigurationData),
    }
  }, [destinationConfigurationData, selectedTarget])

  const effectiveDestinationDataRef = useRef(effectiveDestinationData)
  effectiveDestinationDataRef.current = effectiveDestinationData
  const isExcelDestination = useMemo(() => {
    return effectiveDestinationData?.data_source_instance === 'excel'
  }, [effectiveDestinationData?.data_source_instance])
  const isFileSystemDestination = useMemo(() => {
    const t = (effectiveDestinationData?.data_source_type ?? '').trim().toUpperCase()
    return t === DataSourceType.FILESYSTEM
  }, [effectiveDestinationData?.data_source_type])
  const skipsLoadType = isExcelDestination || isFileSystemDestination

  const handleValidityChange = useCallback(
    (isValid: boolean) => {
      setNodeScopedState(prevState => {
        const key = nodeKeyRef.current
        const previousNodeState = prevState[key] ?? {
          postTestFields: persistedNodeState?.postTestFields ?? [],
          isConnectionTested: persistedNodeState?.isConfigured ?? false,
          isBaseFormValid: false,
          isCombinedFormValid: false,
        }
        const nextBaseFormValid =
          previousNodeState.postTestFields.length > 0 ? previousNodeState.isBaseFormValid : isValid

        if (
          previousNodeState.isCombinedFormValid === isValid &&
          previousNodeState.isBaseFormValid === nextBaseFormValid
        ) {
          return prevState
        }

        return {
          ...prevState,
          [key]: {
            ...previousNodeState,
            isCombinedFormValid: isValid,
            isBaseFormValid: nextBaseFormValid,
          },
        }
      })
    },
    [persistedNodeState?.isConfigured, persistedNodeState?.postTestFields]
  )

  const handleOpenChange = (openState: boolean) => {
    if (!openState) {
      if (isNavigatingBackRef.current) {
        isNavigatingBackRef.current = false
        return
      }
      dispatch(closeOverlayAsync())
    }
  }

  const handleBack = () => {
    isNavigatingBackRef.current = true
    dispatch(popOverlay())
  }

  const handleValuesChange = useCallback((values: Record<string, string>) => {
    latestValuesByNodeRef.current[nodeKeyRef.current] = values
  }, [])

  const schemaFields = useMemo(() => {
    const raw = (
      effectiveDestinationData?.schema_definition as
        | { destination?: SchemaDefinitionField[] }
        | undefined
    )?.destination
    return raw ?? EMPTY_DESTINATION_SCHEMA
  }, [effectiveDestinationData?.schema_definition])

  const effectiveSchemaFields = persistedNodeState?.schemaFields ?? schemaFields
  const isReadonlyConfigured = persistedNodeState?.isReadOnly === true

  const schemaFieldsRef = useRef(effectiveSchemaFields)
  schemaFieldsRef.current = effectiveSchemaFields

  const isPostTestSelectionComplete = useMemo(() => {
    if (isExcelDestination) return isCombinedFormValid
    if (postTestFields.length === 0) return isConnectionTested
    return isCombinedFormValid
  }, [isCombinedFormValid, isConnectionTested, isExcelDestination, postTestFields.length])

  const { mutate: runTestConnection, isPending: isTestConnectionPending } =
    useTestConnectionMutation({
      onSuccess: response => {
        const baseFields = schemaFieldsRef.current
        const maxSchemaOrder = baseFields.reduce((maxOrder, field) => {
          if (typeof field.order !== 'number') return maxOrder
          return Math.max(maxOrder, field.order)
        }, -1)
        const additionalFields = (response.testConnectionResponse ?? []).map((field, index) => ({
          ...field,
          order: maxSchemaOrder + index + 1,
        }))
        setNodeScopedState(prevState => {
          const key = nodeKeyRef.current
          const previousNodeState = prevState[key] ?? {
            postTestFields: persistedNodeState?.postTestFields ?? [],
            isConnectionTested: persistedNodeState?.isConfigured ?? false,
            isBaseFormValid: false,
            isCombinedFormValid: false,
          }

          return {
            ...prevState,
            [key]: {
              ...previousNodeState,
              postTestFields: additionalFields,
              isConnectionTested: true,
              isBaseFormValid: previousNodeState.isCombinedFormValid,
              isCombinedFormValid: false,
            },
          }
        })
      },
    })

  const createTargetDataSourceMutation = useCreateTargetDataSourceMutation()

  const handleTestConnection = useCallback(
    (values: Record<string, string>) => {
      const dynamicConfig = schemaFieldsRef.current.reduce<Record<string, string | null>>(
        (acc, field) => {
          const key = field.key
          if (!key) return acc

          const currentValue = (values[key] ?? '').trim()
          acc[key] = currentValue.length > 0 ? currentValue : null

          return acc
        },
        {}
      )

      runTestConnection({
        databases: [
          {
            type: effectiveDestinationDataRef.current?.data_source_instance ?? '',
            ...dynamicConfig,
          },
        ],
      })
    },
    [runTestConnection]
  )

  const combinedFields = useMemo(
    () => [...effectiveSchemaFields, ...postTestFields],
    [effectiveSchemaFields, postTestFields]
  )

  const postTestFieldsRef = useRef(postTestFields)
  postTestFieldsRef.current = postTestFields

  const handleSave = useCallback(() => {
    const dest = effectiveDestinationDataRef.current
    const nodeId = dest?.nodeId
    if (nodeId == null || dest == null) return

    const valuesRaw = latestValuesByNodeRef.current[nodeKeyRef.current] ?? {}
    const fieldKeys = [...effectiveSchemaFields, ...postTestFieldsRef.current]
      .map(field => field.key)
      .filter((key): key is string => Boolean(key))

    const values: Record<string, string> = {}
    for (const key of fieldKeys) {
      values[key] = typeof valuesRaw[key] === 'string' ? valuesRaw[key].trim() : ''
    }

    const dynamicConfig = fieldKeys.reduce<Record<string, string | null>>((acc, key) => {
      const currentValue = values[key] ?? ''
      acc[key] = currentValue.length > 0 ? currentValue : null
      return acc
    }, {})

    const dataSourceInstance = dest.data_source_instance ?? ''
    const dataSourceType = dest.data_source_type ?? null

    const submittedDestinationPayload = fieldKeys.reduce<
      BaseDatabaseConfig & Record<string, unknown>
    >(
      (acc, key) => {
        acc[key] = values[key]
        return acc
      },
      { type: dataSourceInstance }
    )

    createTargetDataSourceMutation.mutate(
      {
        databases: [
          {
            type: dataSourceInstance,
            ...dynamicConfig,
          },
        ],
      },
      {
        onSuccess: response => {
          const resolvedDestinationName = [response.source_name, values.source_name].find(
            (candidate): candidate is string =>
              typeof candidate === 'string' && candidate.trim().length > 0
          )

          setTargetData(nodeId, {
            ...response,
            data_source_instance: dataSourceInstance,
            data_source_type: dataSourceType,
          })

          const existingNode = getAdpSessionNode(nodeId) ?? {}
          mergeAdpSessionNode(nodeId, {
            ...existingNode,
            destinationConfigurationState: {
              isConfigured: true,
              isReadOnly: true,
              values,
              schemaFields: effectiveSchemaFields.map(f => ({ ...f })),
              postTestFields: postTestFieldsRef.current.map(f => ({ ...f })),
              submitted_destination_payload: submittedDestinationPayload,
              data_source_instance: dataSourceInstance,
              data_source_type: dataSourceType,
            } satisfies PersistedDestinationConfigurationState,
          })

          const snapshot = store.getState()
          const nextNodes = [...snapshot.canvas.present.nodes]
          const destinationNodeIndex = nextNodes.findIndex(
            node => node.id === nodeId && node.type === 'destination'
          )

          if (destinationNodeIndex >= 0) {
            const destinationNode = nextNodes[destinationNodeIndex]
            nextNodes[destinationNodeIndex] = {
              ...destinationNode,
              data: {
                ...destinationNode.data,
                nodeSecondaryTitle: resolvedDestinationName,
                mode: 'new-destination',
              },
            } as typeof destinationNode
          }

          dispatch(
            syncCanvasState({
              nodes: nextNodes,
              edges: snapshot.canvas.present.edges,
            })
          )

          if (skipsLoadType) {
            dispatch(
              openOverlay({
                type: 'output-preview',
                data: {
                  nodeId,
                  data_source_instance: dataSourceInstance,
                  data_source_type: dataSourceType,
                },
              })
            )
            return
          }

          dispatch(
            openOverlay({
              type: 'load-type',
              data: { nodeId },
            })
          )
        },
      }
    )
  }, [
    createTargetDataSourceMutation,
    dispatch,
    effectiveSchemaFields,
    isExcelDestination,
    setTargetData,
  ])

  const handleContinueWhenConfigured = useCallback(() => {
    const dest = effectiveDestinationDataRef.current
    const nodeId = dest?.nodeId
    if (nodeId == null) return

    if (skipsLoadType) {
      dispatch(
        openOverlay({
          type: 'output-preview',
          data: {
            nodeId,
            data_source_instance: dest?.data_source_instance ?? null,
            data_source_type: dest?.data_source_type ?? null,
          },
        })
      )
      return
    }

    dispatch(
      openOverlay({
        type: 'load-type',
        data: { nodeId },
      })
    )
  }, [dispatch, skipsLoadType])

  let primaryFooterLabel = 'Save'
  if (createTargetDataSourceMutation.isPending) {
    primaryFooterLabel = 'Saving...'
  } else if (isReadonlyConfigured) {
    primaryFooterLabel = 'Continue'
  }

  useEffect(() => {
    if (postTestFields.length === 0) return

    const el = formContainerRef.current
    if (!el) return

    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth',
      })
    })
  }, [postTestFields])

  return (
    <Sheet open={isDestinationConfigurationOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="right" noBackdrop topOffset={64} className="shadow-none">
        <SheetHeader>
          <SheetTitle className="text-[18px] font-medium">Destination Configuration</SheetTitle>
        </SheetHeader>
        {effectiveDestinationData && (
          <>
            <div
              ref={formContainerRef}
              className={`flex h-full flex-col gap-4 overflow-y-auto px-3 ${styles.thinScroll}`}
            >
              <PickerDetailsHeader
                type="destination"
                prerequisite={effectiveDestinationData?.prerequisite}
                imageLink={effectiveDestinationData?.image_link}
                sourceName={effectiveDestinationData?.data_source_name}
                label="Destination Name"
              />
              <div className="flex items-center gap-2 pb-2">
                {shouldShowBackButton && (
                  <Button variant="ghost" size="icon" onClick={handleBack}>
                    <ChevronLeft />
                  </Button>
                )}
                <h3 className="text-lg font-medium">Configuration Details</h3>
              </div>
              <SchemaDefinitionForm
                dbName={effectiveDestinationData?.data_source_name}
                type="destination"
                nodeId={destinationConfigurationData?.nodeId}
                fields={combinedFields}
                initialValues={persistedNodeState?.values}
                onSubmit={handleTestConnection}
                onValuesChange={handleValuesChange}
                onValidityChange={handleValidityChange}
                readOnly={isReadonlyConfigured}
                submitDisabled={!isBaseFormValid || isTestConnectionPending || isConnectionTested}
                showSubmitButton={
                  !isExcelDestination && !isConnectionTested && !isReadonlyConfigured
                }
                submitLabel={isTestConnectionPending ? 'Testing...' : 'Test Connection'}
              />
              {isExcelDestination && (
                <p className="text-sm text-muted-foreground -mt-5">
                  <sup className="mr-0.5">*</sup> Excel as destination is best suited for small
                  data.
                </p>
              )}
              {isReadonlyConfigured && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Destination configuration cannot be changed once configured.
                </div>
              )}
            </div>
            <SheetFooter className="grid grid-cols-2 gap-2 border-t px-2 py-3">
              <Button variant="outline" className="w-full" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                className="w-full"
                disabled={
                  (!isReadonlyConfigured &&
                    (!isPostTestSelectionComplete || createTargetDataSourceMutation.isPending)) ||
                  effectiveDestinationData.nodeId == null
                }
                onClick={isReadonlyConfigured ? handleContinueWhenConfigured : handleSave}
              >
                {primaryFooterLabel}
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default DestinationConfiguration
